import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { baseurl } from '../baseurl';
import SideMenu from "../component/SideMenu";
import Topbar from "../component/Topbar";

const Dashboard = () => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const adminToken = localStorage.getItem('adminToken');

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                const response = await axios.get(`${baseurl}getAdminAnalytics`, {
                    headers: { Authorization: `Bearer ${adminToken}` }
                });
                if (response.data.success) {
                    setAnalytics(response.data.analytics);
                }
            } catch (error) {
                console.error("Error fetching analytics", error);
            } finally {
                // Short delay for smooth transition
                setTimeout(() => setLoading(false), 800);
            }
        };
        fetchAnalytics();
    }, [adminToken]);

    // Calculate total projects only if analytics exists
    const totalProjects = analytics ? parseInt(analytics.summary.total_projects) : 0;

    // --- Skeleton UI Helper ---
    const renderSkeleton = () => (
        <div className="skeleton-wrapper">
            <div className="skeleton-header">
                <div className="skeleton-line title"></div>
                <div className="skeleton-line subtitle"></div>
            </div>
            <div className="stats-grid">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="stat-card skeleton-card">
                        <div className="skeleton-circle"></div>
                        <div className="skeleton-info">
                            <div className="skeleton-line small"></div>
                            <div className="skeleton-line medium"></div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="dashboard-mid-section">
                <div className="visual-box skeleton-box">
                    <div className="skeleton-line medium"></div>
                    <div className="skeleton-circle large"></div>
                    <div className="skeleton-line full"></div>
                    <div className="skeleton-line full"></div>
                </div>
                <div className="visual-box skeleton-box">
                    <div className="skeleton-line medium"></div>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="skeleton-line full table-row"></div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="app-container">
            <Topbar />
            <div className="main-wrapper">
                {/* <SideMenu /> */}
                
                <main className="dashboard-content">
                    {loading ? (
                        renderSkeleton()
                    ) : analytics ? (
                        <>
                            <header className="dashboard-header">
                                <div>
                                    <h1>Admin Overview</h1>
                                    <p>Real-time platform performance and activity.</p>
                                </div>
                                <button className="refresh-btn" onClick={() => window.location.reload()}>
                                    <i className="fas fa-sync-alt"></i> Refresh Data
                                </button>
                            </header>

                            {/* KPI CARDS */}
                            <div className="stats-grid">
                                <div className="stat-card">
                                    <div className="stat-icon users"><i className="fas fa-users"></i></div>
                                    <div className="stat-info">
                                        <h3>Total Users</h3>
                                        <p className="stat-value">{analytics.summary.total_users}</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon projects"><i className="fas fa-layer-group"></i></div>
                                    <div className="stat-info">
                                        <h3>Total Projects</h3>
                                        <p className="stat-value">{analytics.summary.total_projects}</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon credits"><i className="fas fa-wallet"></i></div>
                                    <div className="stat-info">
                                        <h3>Outstanding Credits</h3>
                                        <p className="stat-value">{parseFloat(analytics.summary.total_outstanding_credits).toFixed(2)}</p>
                                    </div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-icon subs"><i className="fas fa-check-circle"></i></div>
                                    <div className="stat-info">
                                        <h3>Active Subs</h3>
                                        <p className="stat-value">{analytics.summary.active_subscriptions}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="dashboard-mid-section">
                                {/* VISUAL CHART: PROJECT DISTRIBUTION */}
                                <div className="visual-box chart-container">
                                    <div className="box-header">
                                        <h3>Project Distribution</h3>
                                        <i className="fas fa-chart-pie"></i>
                                    </div>
                                    
                                    <div className="donut-wrapper">
                                        <svg viewBox="0 0 36 36" className="circular-chart">
                                            <path className="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                            <path className="circle completed-stroke" 
                                                strokeDasharray={`${(analytics.projectDistribution.find(d => d.status === 'Completed')?.count / totalProjects) * 100}, 100`}
                                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" 
                                            />
                                        </svg>
                                        <div className="percentage">{totalProjects}</div>
                                    </div>

                                    <div className="status-bars">
                                        {analytics.projectDistribution.map((item, index) => (
                                            <div key={index} className="bar-group">
                                                <div className="bar-info">
                                                    <span>{item.status}</span>
                                                    <span>{item.count}</span>
                                                </div>
                                                <div className="progress-bg">
                                                    <div 
                                                        className={`progress-fill ${item.status.toLowerCase()}`} 
                                                        style={{ width: `${(item.count / totalProjects) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* RECENT ACTIVITY */}
                                <div className="visual-box activity-container">
                                    <div className="box-header">
                                        <h3>Recent Activity</h3>
                                        <i className="fas fa-history"></i>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Project</th>
                                                    <th>User & Company</th>
                                                    <th>Status</th>
                                                    <th>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {analytics.recentActivity.map((activity) => (
                                                    <tr key={activity.project_id}>
                                                        <td><span className="id-badge">#{activity.project_id}</span></td>
                                                        <td>
                                                            <div className="user-cell">
                                                                <strong>{activity.user_name}</strong>
                                                                <span>{activity.company_name}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`status-pill ${activity.status.toLowerCase()}`}>
                                                                {activity.status}
                                                            </span>
                                                        </td>
                                                        <td>{new Date(activity.created_at).toLocaleDateString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div>Error loading data.</div>
                    )}
                </main>
            </div>

            <style>
                {`
                .dashboard-content { flex: 1; padding: 30px; background-color: #f4f7f6; overflow-y: auto; }
                .dashboard-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
                .refresh-btn { padding: 10px 18px; border-radius: 8px; border: 1px solid #ddd; background: white; cursor: pointer; transition: 0.3s; font-weight: 500; }
                .refresh-btn:hover { background: #f0f0f0; }

                /* Stats Grid */
                .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; }
                .stat-card { background: white; padding: 25px; border-radius: 16px; display: flex; align-items: center; box-shadow: 0 10px 20px rgba(0,0,0,0.03); }
                .stat-icon { width: 56px; height: 56px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; margin-right: 20px; }
                .stat-icon.users { background: #e0f2fe; color: #0284c7; }
                .stat-icon.projects { background: #f5f3ff; color: #7c3aed; }
                .stat-icon.credits { background: #fff7ed; color: #ea580c; }
                .stat-icon.subs { background: #f0fdf4; color: #16a34a; }
                .stat-value { font-size: 1.8rem; font-weight: 800; color: #1e293b; margin: 0; }
                .stat-info h3 { font-size: 0.85rem; color: #64748b; margin: 0 0 4px 0; text-transform: uppercase; letter-spacing: 0.5px; }

                /* Dashboard Sections */
                .dashboard-mid-section { display: grid; grid-template-columns: 350px 1fr; gap: 25px; margin-top: 30px; }
                .visual-box { background: white; padding: 25px; border-radius: 16px; box-shadow: 0 10px 20px rgba(0,0,0,0.03); }
                .box-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; }
                .box-header h3 { font-size: 1.1rem; color: #1e293b; margin: 0; }
                .box-header i { color: #cbd5e1; }

                /* Donut Chart SVG */
                .donut-wrapper { position: relative; width: 150px; margin: 0 auto 25px auto; }
                .circular-chart { display: block; margin: 10px auto; max-width: 100%; max-height: 250px; }
                .circle-bg { fill: none; stroke: #eee; stroke-width: 3.8; }
                .circle { fill: none; stroke-width: 3.8; stroke-linecap: round; }
                .completed-stroke { stroke: #16a34a; }
                .percentage { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 1.5rem; font-weight: 700; color: #1e293b; }

                /* Progress Bars */
                .status-bars { display: flex; flex-direction: column; gap: 15px; }
                .bar-info { display: flex; justify-content: space-between; font-size: 0.85rem; margin-bottom: 5px; color: #475569; font-weight: 500; }
                .progress-bg { background: #f1f5f9; height: 8px; border-radius: 10px; overflow: hidden; }
                .progress-fill { height: 100%; border-radius: 10px; transition: width 1s ease-in-out; }
                .progress-fill.completed { background: #16a34a; }
                .progress-fill.pending { background: #eab308; }
                .progress-fill.draft { background: #94a3b8; }

                /* Table Styling */
                .id-badge { background: #f1f5f9; padding: 4px 8px; border-radius: 6px; font-family: monospace; color: #475569; }
                .user-cell { display: flex; flex-direction: column; }
                .user-cell span { font-size: 0.75rem; color: #64748b; }
                .status-pill { padding: 5px 12px; border-radius: 8px; font-size: 0.75rem; font-weight: 700; }
                .status-pill.pending { background: #fef9c3; color: #a16207; }
                .status-pill.completed { background: #dcfce7; color: #166534; }
                .status-pill.draft { background: #f1f5f9; color: #475569; }

                .admin-table { width: 100%; border-collapse: collapse; }
                .admin-table th { text-align: left; padding: 15px; border-bottom: 1px solid #f1f5f9; color: #64748b; font-size: 0.8rem; text-transform: uppercase; }
                .admin-table td { padding: 15px; border-bottom: 1px solid #f1f5f9; font-size: 0.9rem; vertical-align: middle; }

                /* SKELETON UI STYLES */
                .skeleton-line {
                    height: 15px;
                    background: linear-gradient(90deg, #eff1f3 25%, #e2e5e7 50%, #eff1f3 75%);
                    background-size: 400% 100%;
                    animation: skeleton-loading 1.4s ease infinite;
                    border-radius: 4px;
                    margin-bottom: 10px;
                }
                .skeleton-circle {
                    width: 50px;
                    height: 50px;
                    background: #eff1f3;
                    border-radius: 50%;
                    animation: skeleton-loading 1.4s ease infinite;
                    margin-right: 15px;
                }
                .skeleton-circle.large { width: 120px; height: 120px; margin: 20px auto; }
                
                @keyframes skeleton-loading {
                    0% { background-position: 100% 50%; }
                    100% { background-position: 0 50%; }
                }

                .skeleton-header { margin-bottom: 30px; }
                .title { width: 200px; height: 30px; }
                .subtitle { width: 300px; }
                .small { width: 60px; }
                .medium { width: 120px; }
                .full { width: 100%; }
                .table-row { height: 40px; margin-top: 15px; }
                
                .skeleton-card { background: #fff; border: none; }
                .skeleton-info { flex: 1; }
                `}
            </style>
        </div>
    );
};

export default Dashboard;